import { useMsal } from '@azure/msal-react';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router';
import './App.scss';
import { useMsalSessionManagement, useStores } from './common/hooks';
import { signOut } from './common/msal';
import { isPpeEnvironment } from './configurations';
import { RoleName } from './models';
import { AwardResourcesPage } from './pages/page-award-resources/AwardResourcesPage';
import { AwardPage } from './pages/page-award/AwardPage';
import { CountryAwardPage } from './pages/page-country-award/CountryAwardPage';
import { CreateOrEditEmailTemplatePage } from './pages/page-create-or-edit-email-template/CreateOrEditEmailTemplatePage';
import { EditAwardDetailPage } from './pages/page-edit-award-detail/EditAwardDetailPage';
import { EditRegionalPage } from './pages/page-edit-regional/EditRegionalPage';
import { EmailPage } from './pages/page-email/EmailPage';
import HomePage from './pages/page-home/HomePage';
import { JudgingDashboardPage } from './pages/page-judging-dashboard/JudgingDashboardPage';
import { JudgingResourcesPage } from './pages/page-judging-resource/JudgingResourcesPage';
import { ManageAwardListPage } from './pages/page-manage-award-list/ManageAwardListPage';
import { NominationForStaffPage } from './pages/page-nomination-for-staff/NominationForStaffPage';
import { NominationWizardPage } from './pages/page-nomination-wizard/NominationWizardPage';
import NotFoundPage from './pages/page-not-found/NotFoundPage';
import { NotificationsPage } from './pages/page-notifications/NotificationsPage';
import { OperationDashboardPage } from './pages/page-operation-dashboard/OperationDashboardPage';
import { ProgramDashboardPage } from './pages/page-program-dashboard/ProgramDashboardPage';
import { ProgramDatesPage } from './pages/page-program-dates/ProgramDatesPage';
import { ProgramsPage } from './pages/page-programs/ProgramsPage';
import { QuestionnaireWizardPage } from './pages/page-questionnaire-wizard/QuestionnaireWizardPage';
import { ReviewAndSubmitNominationPage } from './pages/page-review-and-submit-nomination/ReviewAndSubmitNominationPage';
import { ReviewQuestionnairePage } from './pages/page-review-questionnaire/ReviewQuestionnairePage';
import { SelectAwardPage } from './pages/page-select-award/SelectAwardPage';
import { UserProfilePage } from './pages/page-user-profile/UserProfilePage';
import { UserResourcesPage } from './pages/page-user-resources/UserResourcesPage';
import { WinnerRequirementPage } from './pages/page-winner-requirement/WinnerRequirementPage';
import { WinnersAndFinalistsPage } from './pages/page-winners-and-finalists/WinnersAndFinalistsPage';
import { ProtectedRoute } from './pages/shared-components';
import Loading from './pages/shared-components/Loading';
import { RootLayout } from './RootLayout';
import { RoutePaths } from './route-paths';

const AppImpl: React.FC = () => {
  const {
    apiStore,
    idleStore,
    userStore,
    awardStore,
    countryStore,
    timeZoneStore,
    targetGroupStore,
    partnerTypeStore,
    notificationStore,
  } = useStores();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNominationWindowClosed, setNominationWindowClosed] = useState(true);
  const [programId, setProgramId] = useState(0);
  const [isShowSubmitNominationSuccessToast, setShowSubmitNominationSuccessToast] = useState(false);
  const [isShowSubmitQuestionnaireSuccessToast, setShowSubmitQuestionnaireSuccessToast] = useState(false);

  const {
    instance,
  } = useMsal();

  const activeAccount = instance.getActiveAccount();

  const updateCurrentUser = async (): Promise<void> => {
    try {
      await apiStore.updateCurrentUser(instance);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getUserInfo = async (): Promise<void> => {
    if (!activeAccount) return;

    idleStore.setUserStorageKey(activeAccount.username);
    idleStore.start();

    const needUpdateUserInfo = userStore.needUpdateUserInfo(activeAccount.username);
    if (needUpdateUserInfo) {
      await updateCurrentUser();
      userStore.setLastUpdateUserInfo(activeAccount.username);
    }

    try {
      const userInfo = await apiStore.getCurrentUser(instance);
      userStore.setCurrentUser(userInfo);
      userStore.setAccountHasAccess(userInfo.hasPcAccount);
      userStore.setIsShowPartnerPage(!userStore.isStaff);
    } catch (error) {
      // TODO: Handle error
    }

    if (isPpeEnvironment) {
      try {
        const [
          rolesResult,
          impersonationResult,
        ] = await Promise.allSettled([
          apiStore.getUserImpersonationRoles(instance),
          apiStore.getUserImpersonation(instance),
        ]);

        rolesResult.status === 'fulfilled' && userStore.setRoles(rolesResult.value);
        impersonationResult.status === 'fulfilled' && userStore.setSelectedRole(impersonationResult.value);
      } catch (error) {
        // TODO: Handle error
      }
    }
  };

  const getDefaultProgramRef = async (): Promise<void> => {
    try {
      const programRef = await apiStore.getDefaultProgramRef(instance);
      awardStore.setDefaultProgramRef(programRef);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const getProgramDates = async (): Promise<void> => {
    try {
      const currentDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss.SSS XXX');
      const programDates = await apiStore.getProgramDates(instance, currentDate, currentDate);
      awardStore.setProgramDates(programDates);
      setNominationWindowClosed(!awardStore.isNominationWindowOpen);
      if (awardStore.programDates.length > 0) setProgramId(awardStore.programDates[0].programId);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const getUserEORestrictionStatus = async (): Promise<void> => {
    try {
      const isUserRestricted = await apiStore.getUserEORestrictionStatus(instance);
      userStore.setIsUserSubjectToEORestrictions(isUserRestricted);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const updatePartnerAccounts = async (): Promise<void> => {
    try {
      await apiStore.updatePartnerAccounts(instance);
    } catch (error) {
      // TODO: Handle error
    }
  }

  const initializeApp = async (): Promise<void> => {
    setIsLoading(true);

    await Promise.allSettled([
      getUserEORestrictionStatus(),
      getUserInfo(),
      getProgramDates(),
      getDefaultProgramRef(),
      updatePartnerAccounts(),
    ]);

    if (userStore.isAdmin) {
      const [
        timeZonesResult,
        targetGroupsResult,
        notificationTypesResult,
        notificationLocationsResult,
      ] = await Promise.allSettled([
        apiStore.getTimeZones(instance),
        apiStore.getTargetGroups(instance),
        apiStore.getNotificationTypes(instance),
        apiStore.getNotificationLocations(instance),
      ]);

      timeZonesResult.status === 'fulfilled' && timeZoneStore.setTimeZones(timeZonesResult.value);
      targetGroupsResult.status === 'fulfilled' && targetGroupStore.setTargetGroups(targetGroupsResult.value);
      notificationTypesResult.status === 'fulfilled' && notificationStore.setNotificationTypes(notificationTypesResult.value);
      notificationLocationsResult.status === 'fulfilled' && notificationStore.setNotificationLocations(notificationLocationsResult.value);
    }

    const [
      partnerTypeOptionsResult,
      awardCountriesResult,
    ] = await Promise.allSettled([
      apiStore.getPartnerTypeOptions(instance),
      apiStore.getCountries(instance, 'awards'),
    ]);

    partnerTypeOptionsResult.status === 'fulfilled' && partnerTypeStore.setPartnerTypeOptions(partnerTypeOptionsResult.value);
    awardCountriesResult.status === 'fulfilled' && countryStore.setCountries('awards', awardCountriesResult.value);

    setIsLoading(false);
  };

  const cleanupAndSignOut = (): void => {
    if (activeAccount) {
      userStore.removeLastUpdateUserInfo(activeAccount.username);
      idleStore.removeActivity();
    }
    signOut(instance);
  };

  useMsalSessionManagement(instance, cleanupAndSignOut);

  useEffect(() => {
    if (idleStore.isIdle) {
      cleanupAndSignOut();
    }

    return () => {
      idleStore.stop();
    };
  }, [idleStore.isIdle]);

  useEffect(() => {
    initializeApp();
  }, [userStore.selectedRole]);

  if (!userStore.userInfo || isLoading) {
    return (
      <div className='loading-frame'>
        <Loading />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path='/'
        element={<RootLayout />}
      >
        <Route
          path={RoutePaths.Home}
          element={
            <HomePage
              programId={programId}
              isShowSubmitNominationSuccessToast={isShowSubmitNominationSuccessToast}
            />
          }
        />
        <Route
          path={RoutePaths.ProgramDashboard}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.GroupLead,
                  RoleName.AwardLead,
                  RoleName.RegionLead,
                  RoleName.CountryLead,
                  RoleName.AwardNominationReviewer,
                  RoleName.CountryNominationReviewer,
                ]
              }
            >
              <ProgramDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.JudgingDashboard}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Judge,
                  RoleName.NominationJudge,
                ]
              }
            >
              <JudgingDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Awards}/:awardId`}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.GroupLead,
                  RoleName.AwardLead,
                  RoleName.AwardNominationReviewer
                ]
              }
            >
              <AwardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.EditAwardDetail}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <EditAwardDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.ManageAwardList}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <ManageAwardListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.EditRegional}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.RegionLead
                ]
              }
            >
              <EditRegionalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.Programs}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <ProgramsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.Email}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <EmailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Email}/create`}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <CreateOrEditEmailTemplatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Email}/:emailTemplateId/edit`}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <CreateOrEditEmailTemplatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.Notifications}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.UserResources}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <UserResourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.WinnerRequirement}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <WinnerRequirementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.CountryAward}/:countryId`}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.RegionLead,
                  RoleName.CountryLead,
                  RoleName.CountryNominationReviewer,
                ]
              }
            >
              <CountryAwardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.ProgramDates}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <ProgramDatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.SelectAward}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && !isNominationWindowClosed}
              fallback='no-account'
            >
              <SelectAwardPage
                loggedInUserId={userStore.userInfo!.userId}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.Nominate}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && !isNominationWindowClosed}
              fallback='no-account'
            >
              <NominationWizardPage userInfo={userStore.userInfo!} />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Nominations}/:nominationId`}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && !isNominationWindowClosed}
              fallback='redirect-home'
            >
              <NominationWizardPage userInfo={userStore.userInfo!} />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Awards}/:awardId${RoutePaths.Nominations}/:nominationId`}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.GroupLead,
                  RoleName.AwardLead,
                  RoleName.RegionLead,
                  RoleName.CountryLead,
                  RoleName.AwardNominationReviewer,
                  RoleName.CountryNominationReviewer,
                  RoleName.Judge,
                ]
              }
            >
              <NominationForStaffPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Awards}/:awardId${RoutePaths.Nominations}/:nominationId${RoutePaths.Countries}/:countryId`}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.GroupLead,
                  RoleName.AwardLead,
                  RoleName.RegionLead,
                  RoleName.CountryLead,
                  RoleName.AwardNominationReviewer,
                  RoleName.CountryNominationReviewer,
                  RoleName.Judge,
                ]
              }
            >
              <NominationForStaffPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.ReviewAndSubmit}/:nominationId`}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && !isNominationWindowClosed}
              fallback='no-account'
            >
              <ReviewAndSubmitNominationPage
                onSubmitNominationSuccess={() => {
                  setShowSubmitNominationSuccessToast(true);
                  setTimeout(() => setShowSubmitNominationSuccessToast(false), 4000);
                }}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Winners}/:winnerId/questionnaire`}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && awardStore.isWinnersWindowOpen}
              fallback='redirect-home'
            >
              <QuestionnaireWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.Questionnaires}/:questionnaireId`}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && awardStore.isWinnersWindowOpen}
              fallback='redirect-home'
            >
              <QuestionnaireWizardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${RoutePaths.ReviewQuestionnaire}/:questionnaireId`}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess && awardStore.isWinnersWindowOpen}
              fallback='redirect-home'
            >
              <ReviewQuestionnairePage onSubmitQuestionnaireSuccess={() => {
                setShowSubmitQuestionnaireSuccessToast(true);
                setTimeout(() => setShowSubmitQuestionnaireSuccessToast(false), 4000);
              }} />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.AwardResources}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess}
              fallback='no-account'
            >
              <AwardResourcesPage
                isShowSubmitQuestionnaireSuccessToast={isShowSubmitQuestionnaireSuccessToast}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.JudgingResources}
          element={
            <ProtectedRoute
              allowedRoles={
                [
                  RoleName.Admin,
                  RoleName.GroupLead,
                  RoleName.AwardLead,
                  RoleName.RegionLead,
                  RoleName.CountryLead,
                  RoleName.AwardNominationReviewer,
                  RoleName.CountryNominationReviewer,
                  RoleName.Judge,
                  RoleName.NominationJudge,
                ]
              }
            >
              <JudgingResourcesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.UserProfile}
          element={
            <ProtectedRoute
              allowedRoles={[RoleName.Partner]}
              accessCheck={userStore.accountHasAccess}
              fallback='no-account'
            >
              <UserProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.WinnersAndFinalists}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <WinnersAndFinalistsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.OperationDashboard}
          element={
            <ProtectedRoute allowedRoles={[RoleName.Admin]}>
              <OperationDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={RoutePaths.NotFound}
          element={<NotFoundPage />}
        />
        <Route
          path='*'
          element={<NotFoundPage />}
        />
      </Route>
    </Routes>
  );
}

export const App = observer(AppImpl);
